/**
 * @file ui/src/pages/KneAdmin.tsx
 * @description Spark 2.0 KNE Governance Console
 * @copyright 2026 Seth Zhai. BSL 1.1
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/auth'
import { useAdminCopy, useAdminI18n } from '../adminI18n'
import styles from './Admin.module.css'

// ── Types ─────────────────────────────────────────────────────────────────────

interface BetaMetrics {
  coverageMatrix: Array<{
    council: string
    canSupportSourceSupported: boolean
    supportedSourceCount: number
    structuredSegmentSourceCount: number
    supportingSourceIds: string[]
    supportingSourceNames: string[]
    topSourceMissingScenario: string
    nextMissingSourceType: string
  }>
  sourceGapRanking: Array<{
    domain: string
    council: string
    claimType: string
    trustState: string
    count: number
    priority: 'high' | 'medium' | 'low'
    nextAction: string
    missingSourceType: string
    sampleClaim: string
  }>
  sourceHintWithoutSupport7d: number
  sourceHintCoverageRate: number | null
  verifyCount7d: number
  sourceGapTasks: Array<{
    id: string
    createdAt: string
    userId: string
    domain: string
    council: string
    claimType: string
    trustState: string
    priority: string
    missingSourceType: string
    taskStatus: string
    taskReviewNote: string
    taskReviewedBy: string
    taskReviewedAt: string
    sampleClaim: string
    nextAction: string
  }>
}

interface ValidationInviteCode {
  code: string
  maxUses: number
  usedCount: number
  note: string | null
}

interface KNEQualityMetrics {
  scope: 'organic' | 'paid_validation'
  inviteCode: string | null
  windowDays: number
  verifyCount: number
  userCount: number
  trustStates: Record<string, number>
  sourceDetailsOpened: number
  evidenceSourceOpened: number
  wrongSourcesFeedback: number
  supportingCount: number
  backgroundCount: number
  irrelevantCandidateCount: number
  retrievalVersions: Array<{ retrievalVersion: string; count: number }>
  sourceGapRanking: BetaMetrics['sourceGapRanking']
  coverageMatrix: BetaMetrics['coverageMatrix']
  shadow: {
    retrievalVersion: string
    completed: number
    failed: number
    degraded: number
    sourceCoverageChanged: number
    trustStateChanged: number
    averageLatencyMs: number
    lastSeenAt: string
  }
}

type KNEContributionAction =
  | 'submit_claim'
  | 'correct_claim'
  | 'add_source'
  | 'challenge_claim'
  | 'support_claim'

type KNEContributionPlatform = 'chatgpt' | 'claude' | 'doubao' | 'qwen' | 'web' | 'unknown'

interface KNEContribution {
  id: string
  userId: string
  action: KNEContributionAction
  status: 'pending' | 'accepted' | 'rejected' | 'superseded'
  claimText?: string
  sourceUrl?: string
  sourceTitle?: string
  sourcePlatform?: KNEContributionPlatform
  sourcePageUrl?: string
  sourcePageTitle?: string
  sourceHintLabel?: string
  sourceHintAuthority?: string
  sourceHintType?: string
  sourceHintReason?: string
  correctionText?: string
  challengeReason?: string
  note?: string
  createdAt: string
  updatedAt: string
}

interface KNESourceRegistryEntry {
  origin?: 'accepted_contribution'
  contributionId?: string
  submittedBy?: string
  claimText?: string
  sourcePageUrl?: string
  createdAt?: string
  updatedAt?: string
  sourceId: string
  sourceName: string
  aliases: string[]
  urlHosts?: string[]
  sourceType: string
  authority: string
  supportedCouncils: string[]
  supportedEvidenceTypes: string[]
  canSupportClaim: boolean
  defaultTrustState: string
  reviewRequired: boolean
  freshnessPolicy: string
  notes: string
}

interface KNEEvidenceSegmentReviewItem {
  id: string
  legacyKnowledgeId: string
  sourceId?: string
  sourceName?: string
  sourceTitle: string
  sourceUrl: string
  excerpt: string
  locator: string
  domain: string
  council: string
  secondaryCouncil?: string
  sourceType: string
  sourceAuthority: string
  relation: string
  reviewStatus: 'unreviewed' | 'reviewed' | 'rejected'
  reviewNote?: string
  reviewedBy?: string
  reviewedAt?: string
  createdAt: string
}

type KNECoreTemplateStatus =
  | 'draft'
  | 'reviewed_template'
  | 'core_candidate'
  | 'accepted_candidate'
  | 'rejected'

interface KNECoreTemplateDraft {
  id: string
  createdAt: string
  createdBy: string
  claimText: string
  domain: string
  council: string
  verificationContext: string
  trustState: 'source_supported' | 'needs_review' | 'high_risk' | 'conflicted'
  evidenceSegmentIds: string[]
  reviewNote: string
  coreReviewDecision?: string
  coreReviewNote?: string
  coreReviewedBy?: string
  coreReviewedAt?: string
  coreReviewHistory?: Array<{
    id: string
    createdAt: string
    reviewedBy: string
    decision: string
    reviewNote: string
    nextTemplateStatus: KNECoreTemplateStatus
    reviewLayer: string
    writesFormalKNECore: boolean
  }>
  statusReviewHistory?: Array<{
    id: string
    createdAt: string
    reviewedBy: string
    fromStatus: KNECoreTemplateStatus
    nextTemplateStatus: KNECoreTemplateStatus
    reviewNote: string
    reviewLayer: string
    writesFormalKNECore: boolean
  }>
  templateStatus: KNECoreTemplateStatus
  version: string
  reviewPath?: Array<{
    evidenceSegmentId: string
    sourceId?: string
    sourceName?: string
    sourceUrl?: string
    sourceType?: string
    sourceAuthority?: string
    relation?: string
    reviewStatus?: string
    qualityChecks?: string[]
  }>
}

interface KNECoreTemplateCandidate {
  claimText: string
  domain: string
  council: string
  verificationContext: string
  trustState: KNECoreTemplateDraft['trustState']
  evidenceSegmentIds: string[]
  reviewNote: string
  sourceTitle?: string
  sourceUrl?: string
  sourceId?: string
  sourceAuthority?: string
  sourceType?: string
  qualityChecks?: string[]
}

const KNE_TEMPLATE_STATUSES: KNECoreTemplateStatus[] = [
  'draft',
  'reviewed_template',
  'core_candidate',
  'accepted_candidate',
  'rejected',
]

function normalizeTemplateStatus(value: string): KNECoreTemplateStatus {
  return KNE_TEMPLATE_STATUSES.includes(value as KNECoreTemplateStatus)
    ? value as KNECoreTemplateStatus
    : 'draft'
}

function templateStatusLabel(status: KNECoreTemplateStatus, t: (value: string) => string) {
  const labels: Record<KNECoreTemplateStatus, string> = {
    draft: '草稿',
    reviewed_template: '已复核草稿',
    core_candidate: '核心候选',
    accepted_candidate: '二审通过候选',
    rejected: '已拒绝',
  }
  return t(labels[status])
}

function trustStateLabel(state: string, t: (value: string) => string) {
  const labels: Record<string, string> = {
    source_supported: '来源支持',
    source_missing: '证据不足',
    needs_review: '待复核',
    high_risk: '高风险',
    conflicted: '存在冲突',
    outdated: '可能过期',
    not_knowledge: '非知识内容',
  }
  return t(labels[state] ?? state)
}

function sourceGapTaskStatusLabel(status: string, t: (value: string) => string) {
  const labels: Record<string, string> = {
    open: '未处理',
    in_progress: '处理中',
    done: '已完成',
    ignored: '已忽略',
  }
  return t(labels[status] ?? status)
}

function priorityLabel(priority: string, t: (value: string) => string) {
  const labels: Record<string, string> = {
    high: '高',
    medium: '中',
    low: '低',
  }
  return t(labels[priority] ?? priority)
}

const PLATFORM_LABEL: Record<KNEContributionPlatform, string> = {
  chatgpt: 'ChatGPT',
  qwen: 'Qwen',
  claude: 'Claude',
  doubao: 'Doubao',
  web: 'Web',
  unknown: 'Unknown',
}

const ACTION_LABEL: Record<KNEContributionAction, string> = {
  add_source: '补充来源',
  challenge_claim: '质疑主张',
  correct_claim: '纠正主张',
  submit_claim: '提交主张',
  support_claim: '支持主张',
}

const ACTION_TONE: Record<KNEContributionAction, string> = {
  add_source: 'source',
  challenge_claim: 'risk',
  correct_claim: 'review',
  submit_claim: 'claim',
  support_claim: 'support',
}

// ── API helpers ───────────────────────────────────────────────────────────────

function useAdminApi() {
  const { token } = useAuth()
  const base    = `/v1/admin`
  const headers = {
    'Content-Type': 'application/json',
    Authorization:  `Bearer ${token ?? ''}`,
  }
  const get = async (path: string) => {
    const res = await fetch(`${base}${path}`, { headers })
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
    return res.json()
  }
  const patch = async (path: string, body?: unknown) => {
    const res = await fetch(`${base}${path}`, {
      method: 'PATCH', headers,
      body: body ? JSON.stringify(body) : undefined,
    })
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
    return res.json()
  }
  const post = async (path: string, body: unknown) => {
    const res = await fetch(`${base}${path}`, {
      method: 'POST', headers,
      body: JSON.stringify(body),
    })
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
    return res.json()
  }
  return { get, patch, post }
}

function useKNEAdminApi() {
  const { token } = useAuth()
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token ?? ''}`,
  }
  const get = async (path: string) => {
    const res = await fetch(`/api/v1/kne${path}`, { headers })
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
    return res.json()
  }
  const post = async (path: string, body: unknown) => {
    const res = await fetch(`/api/v1/kne${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    })
    if (!res.ok) throw new Error(`${res.status} ${await res.text()}`)
    return res.json()
  }
  return { get, post }
}

// ── Spark 2.0 KNE 工作台 ─────────────────────────────────────────────────────

function KNEOverviewView() {
  const copy = useAdminCopy()
  const t = copy.t
  const api = useAdminApi()
  const [metrics, setMetrics] = useState<KNEQualityMetrics | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.get('/kne-quality-metrics?days=14')
      .then((data: { metrics: KNEQualityMetrics }) => setMetrics(data.metrics))
      .catch((loadError: Error) => setError(loadError.message))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const cards = [
    {
      label: '生产验证',
      value: metrics?.verifyCount ?? '—',
      detail: '近 14 天自然用户与匿名试用的生产 Verify；不包含付费邀请码队列。',
    },
    {
      label: '外部证据点击',
      value: metrics?.evidenceSourceOpened ?? '—',
      detail: '用户实际离开 Spark 打开来源链接；与展开来源详情分开。',
    },
    {
      label: '支持 / 背景 / 无关',
      value: metrics ? `${metrics.supportingCount} / ${metrics.backgroundCount} / ${metrics.irrelevantCandidateCount}` : '—',
      detail: '检索候选经过 Evidence Fit 后的公开分类；无关候选必须保持低位。',
    },
    {
      label: '错误来源反馈',
      value: metrics?.wrongSourcesFeedback ?? '—',
      detail: '用户明确标记来源错误或不适用；这是检索质量的直接负向信号。',
    },
  ]

  return (
    <div className={styles.stack}>
      <section className={styles.heroPanel}>
        <div>
          <p className={styles.kicker}>KNE Quality & Governance</p>
          <h1 className={styles.heroTitle}>{t('KNE 检索质量与治理')}</h1>
          <p className={styles.heroText}>
            {t('只展示 KNE 检索、证据适用性、Trust State、来源缺口和治理状态；账号增长与发布状态留在 Spark Admin。')}
          </p>
        </div>
        <div className={styles.heroMetrics}>
          <div>
            <span>{metrics?.userCount ?? '—'}</span>
            <small>{t('自然验证用户')}</small>
          </div>
          <div>
            <span>{metrics?.sourceGapRanking.length ?? '—'}</span>
            <small>{t('活跃来源缺口')}</small>
          </div>
          <div>
            <span>{metrics?.shadow.failed ?? '—'}/{metrics?.shadow.degraded ?? '—'}</span>
            <small>{t('Shadow 失败 / 降级')}</small>
          </div>
        </div>
      </section>

      {error ? <div className={styles.errorBanner}>⚠ {error}</div> : null}

      <div className={styles.moduleGrid}>
        {cards.map(card => (
          <article key={card.label} className={styles.moduleCard}>
            <span className={styles.moduleLabel}>{t(card.label)}</span>
            <strong>{typeof card.value === 'number' ? card.value : t(card.value)}</strong>
            <p>{t(card.detail)}</p>
          </article>
        ))}
      </div>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('KNEAdmin 数据边界')}</h2>
        </div>
        <div className={styles.checklist}>
          {[
            ['检索质量', '区分支持来源、背景来源、无关候选、外部证据点击和用户来源错误反馈。'],
            ['Shadow 观察', `当前 ${metrics?.shadow.retrievalVersion ?? 'unknown'}：完成 ${metrics?.shadow.completed ?? 0}，失败 ${metrics?.shadow.failed ?? 0}，降级 ${metrics?.shadow.degraded ?? 0}。`],
            ['来源缺口', '自然流量缺口与付费验证缺口可分别查看；用户行为只提供需求信号，不直接认证知识。'],
            ['治理写入', '来源、证据片段、贡献和 KNE Core 候选仍沿用人工复核，不因检索命中自动晋升。'],
          ].map(([title, body]) => (
            <div key={title} className={styles.checkItem}>
              <span />
              <div>
                <strong>{t(title)}</strong>
                <p>{t(body)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}

function RetrievalQualityView() {
  const copy = useAdminCopy()
  const t = copy.t
  const api = useAdminApi()
  const [inviteCodes, setInviteCodes] = useState<ValidationInviteCode[]>([])
  const [selectedInviteCode, setSelectedInviteCode] = useState('')
  const [metrics, setMetrics] = useState<KNEQualityMetrics | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadMetrics = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const query = new URLSearchParams({ days: '14' })
      if (selectedInviteCode) query.set('inviteCode', selectedInviteCode)
      const data = await api.get(`/kne-quality-metrics?${query.toString()}`) as { metrics: KNEQualityMetrics }
      setMetrics(data.metrics)
    } catch (loadError) {
      setError((loadError as Error).message)
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInviteCode])

  useEffect(() => {
    void api.get('/invite-codes')
      .then((data: { codes: ValidationInviteCode[] }) => setInviteCodes(data.codes))
      .catch((loadError: Error) => setError(loadError.message))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { void loadMetrics() }, [loadMetrics])

  const trustRows = Object.entries(metrics?.trustStates ?? {}).sort((left, right) => right[1] - left[1])
  const fmt = (value: string) => value ? new Date(value).toLocaleString('zh-CN') : '—'

  return (
    <div className={styles.stack}>
      <section className={styles.heroPanel}>
        <div>
          <p className={styles.kicker}>Retrieval Quality</p>
          <h1 className={styles.heroTitle}>{t('检索与证据质量')}</h1>
          <p className={styles.heroText}>{t('查看选定人群中的召回结果、Evidence Fit 分类、Trust State、来源交互和来源缺口。')}</p>
        </div>
        <div className={styles.heroMetrics}>
          <div><span>{metrics?.verifyCount ?? '—'}</span><small>Verify</small></div>
          <div><span>{metrics?.evidenceSourceOpened ?? '—'}</span><small>{t('证据点击')}</small></div>
          <div><span>{metrics?.wrongSourcesFeedback ?? '—'}</span><small>{t('错误来源反馈')}</small></div>
        </div>
      </section>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <div>
            <h2 className={styles.tableTitle}>{t('观察范围')}</h2>
            <p className={styles.sectionHint}>{selectedInviteCode ? t('当前只看所选付费验证队列。') : t('当前只看自然注册与匿名试用。')}</p>
          </div>
          <div className={styles.filterRow}>
            <select className={styles.searchInput} value={selectedInviteCode} onChange={event => setSelectedInviteCode(event.target.value)}>
              <option value="">{t('自然流量')}</option>
              {inviteCodes.map(invite => <option key={invite.code} value={invite.code}>{invite.code} · {invite.usedCount}/{invite.maxUses}</option>)}
            </select>
            <button className={styles.searchBtn} disabled={loading} onClick={() => { void loadMetrics() }}>{loading ? t('刷新中…') : t('刷新')}</button>
          </div>
        </div>
        {error ? <div className={styles.errorBanner}>⚠ {error}</div> : null}
        <div className={styles.moduleGrid}>
          {[
            ['支持来源', metrics?.supportingCount ?? '—', '可直接支持主张的公开证据。'],
            ['背景来源', metrics?.backgroundCount ?? '—', '相关但不能直接支持的上下文。'],
            ['无关候选', metrics?.irrelevantCandidateCount ?? '—', '应被过滤且不能公开冒充相关证据。'],
            ['来源面板 / 外链', metrics ? `${metrics.sourceDetailsOpened} / ${metrics.evidenceSourceOpened}` : '—', '展开详情与实际打开来源分开。'],
          ].map(([label, value, detail]) => (
            <article key={label} className={styles.moduleCard}><span className={styles.moduleLabel}>{t(String(label))}</span><strong>{String(value)}</strong><p>{t(String(detail))}</p></article>
          ))}
        </div>
      </section>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}><h2 className={styles.tableTitle}>Trust State & Retrieval Version</h2></div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead><tr><th>Trust State</th><th>{t('次数')}</th><th>{t('检索版本')}</th><th>{t('次数')}</th></tr></thead>
            <tbody>
              {Math.max(trustRows.length, metrics?.retrievalVersions.length ?? 0) === 0 ? <tr><td colSpan={4}>{t('暂无质量数据')}</td></tr> : Array.from({ length: Math.max(trustRows.length, metrics?.retrievalVersions.length ?? 0) }).map((_, index) => (
                <tr key={index}><td>{trustRows[index]?.[0] ?? '—'}</td><td>{trustRows[index]?.[1] ?? '—'}</td><td className={styles.emailCell}>{metrics?.retrievalVersions[index]?.retrievalVersion ?? '—'}</td><td>{metrics?.retrievalVersions[index]?.count ?? '—'}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <div><h2 className={styles.tableTitle}>R7 Shadow</h2><p className={styles.sectionHint}>{t('Shadow 是工程比较数据，不是用户价值证据。')}</p></div>
        </div>
        <div className={styles.evidenceGrid}>
          <div><span>{t('版本')}</span><p>{metrics?.shadow.retrievalVersion ?? '—'}</p></div>
          <div><span>{t('完成 / 失败 / 降级')}</span><p>{metrics ? `${metrics.shadow.completed} / ${metrics.shadow.failed} / ${metrics.shadow.degraded}` : '—'}</p></div>
          <div><span>{t('覆盖变化 / 状态变化')}</span><p>{metrics ? `${metrics.shadow.sourceCoverageChanged} / ${metrics.shadow.trustStateChanged}` : '—'}</p></div>
          <div><span>{t('平均延迟')}</span><p>{metrics ? `${metrics.shadow.averageLatencyMs} ms` : '—'}</p></div>
          <div><span>{t('最近出现')}</span><p>{metrics ? fmt(metrics.shadow.lastSeenAt) : '—'}</p></div>
        </div>
      </section>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}><h2 className={styles.tableTitle}>{t('当前来源缺口')}</h2></div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead><tr><th>Council</th><th>{t('领域')}</th><th>{t('主张类型')}</th><th>Trust State</th><th>{t('次数')}</th><th>{t('样例')}</th></tr></thead>
            <tbody>
              {(metrics?.sourceGapRanking ?? []).length === 0 ? <tr><td colSpan={6}>{t('暂无来源缺口')}</td></tr> : metrics!.sourceGapRanking.map(gap => (
                <tr key={`${gap.council}-${gap.domain}-${gap.claimType}-${gap.trustState}`}><td>{gap.council}</td><td>{gap.domain}</td><td>{gap.claimType}</td><td>{gap.trustState}</td><td>{gap.count}</td><td>{gap.sampleClaim || '—'}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

function SourceOpsView() {
  const copy = useAdminCopy()
  const t = copy.t
  const api = useKNEAdminApi()
  const [entries, setEntries] = useState<KNESourceRegistryEntry[]>([])
  const [candidateEntries, setCandidateEntries] = useState<KNESourceRegistryEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sourceType, setSourceType] = useState('all')
  const [authority, setAuthority] = useState('all')
  const [search, setSearch] = useState('')

  const loadRegistry = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const params = new URLSearchParams()
      if (sourceType !== 'all') params.set('sourceType', sourceType)
      if (authority !== 'all') params.set('authority', authority)
      if (search.trim()) params.set('search', search.trim())
      const query = params.toString()
      const data = await api.get(`/source-registry${query ? `?${query}` : ''}`) as {
        entries: KNESourceRegistryEntry[]
        candidateEntries: KNESourceRegistryEntry[]
        count: number
        candidateCount: number
        total: number
      }
      setEntries(data.entries)
      setCandidateEntries(data.candidateEntries ?? [])
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceType, authority, search])

  useEffect(() => { loadRegistry() }, [loadRegistry])
  const displayAliases = (aliases: string[]) => {
    const visible = copy.locale === 'en'
      ? aliases.filter(alias => !/[\u4e00-\u9fff]/.test(alias))
      : aliases
    return visible.length > 0 ? visible.join(', ') : '—'
  }

  return (
    <div className={styles.stack}>
      <section className={styles.heroPanel}>
        <div>
          <p className={styles.kicker}>Source Layer</p>
          <h1 className={styles.heroTitle}>{t('来源库维护')}</h1>
          <p className={styles.heroText}>
            {t('优先维护权威来源、来源类型、适用学科和失效风险。旧 Knowledge 只作为 Reference Corpus，不直接升级为认证知识。')}
          </p>
        </div>
        <div className={styles.heroMetrics}>
          <div>
            <span>{entries.length}</span>
            <small>{t('当前显示')}</small>
          </div>
          <div>
            <span>{entries.filter(entry => entry.canSupportClaim).length}</span>
            <small>{t('可支持主张')}</small>
          </div>
          <div>
            <span>{entries.filter(entry => entry.reviewRequired).length}</span>
            <small>{t('需复核')}</small>
          </div>
          <div>
            <span>{candidateEntries.length}</span>
            <small>{t('候选来源')}</small>
          </div>
        </div>
      </section>

      {error && (
        <div className={styles.errorBanner}>
          ⚠ {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('来源库')}</h2>
          <div className={styles.controlBar}>
            <input
              className={styles.searchInput}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('搜索来源')}
            />
            <select className={styles.searchInput} value={sourceType} onChange={e => setSourceType(e.target.value)}>
              {['all', 'official', 'academic_paper', 'standard', 'encyclopedia', 'llm_generated', 'unknown'].map(value => (
                <option key={value} value={value}>{value === 'all' ? t('全部类型') : value}</option>
              ))}
            </select>
            <select className={styles.searchInput} value={authority} onChange={e => setAuthority(e.target.value)}>
              {['all', 'authoritative', 'strong', 'moderate', 'weak', 'unknown'].map(value => (
                <option key={value} value={value}>{value === 'all' ? t('全部权威等级') : value}</option>
              ))}
            </select>
            <button className={styles.searchBtn} onClick={loadRegistry}>{t('刷新')}</button>
          </div>
        </div>

        <div className={styles.reviewBoard}>
          {loading ? (
            <div className={styles.emptyPanel}><span className={styles.pulse}>{t('加载中…')}</span></div>
          ) : entries.length === 0 ? (
            <div className={styles.emptyPanel}>{t('暂无来源注册项。')}</div>
          ) : entries.map(entry => (
            <article key={entry.sourceId} className={styles.contributionCard}>
              <div className={styles.contributionHead}>
                <div>
                  <span className={`${styles.actionPill} ${entry.authority === 'authoritative' ? styles.tone_source : styles.tone_review}`}>
                    {entry.authority}
                  </span>
                  <span className={styles.platformPill}>{entry.sourceType}</span>
                  {entry.canSupportClaim && <span className={styles.platformPill}>{t('可支持主张')}</span>}
                  {entry.reviewRequired && <span className={styles.platformPill}>{t('需复核')}</span>}
                </div>
                <div className={styles.metaLine}>{entry.sourceId} · {entry.freshnessPolicy}</div>
              </div>
              <div className={styles.claimBlock}>
                <span>{entry.urlHosts?.join(' · ') ?? 'source'}</span>
                <p>{entry.sourceName}</p>
              </div>
              <div className={styles.evidenceGrid}>
                <div>
                  <span>{t('议会')}</span>
                  <p>{entry.supportedCouncils.length > 0 ? entry.supportedCouncils.join(', ') : '—'}</p>
                </div>
                <div>
                  <span>{t('证据')}</span>
                  <p>{entry.supportedEvidenceTypes.join(', ')}</p>
                </div>
                <div>
                  <span>{t('别名')}</span>
                  <p>{displayAliases(entry.aliases)}</p>
                </div>
              </div>
              <div className={styles.reasonBlock}>
                <p>{entry.notes}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('候选来源')}</h2>
          <p className={styles.sectionHint}>
            {t('已通过的用户来源贡献只进入候选区；完成人工编目和来源质量评估前，不能支持 KNE 可信判定。')}
          </p>
        </div>
        <div className={styles.reviewBoard}>
          {loading ? (
            <div className={styles.emptyPanel}><span className={styles.pulse}>{t('加载中…')}</span></div>
          ) : candidateEntries.length === 0 ? (
            <div className={styles.emptyPanel}>{t('暂无已通过的候选来源。')}</div>
          ) : candidateEntries.map(entry => (
            <article key={entry.sourceId} className={styles.contributionCard}>
              <div className={styles.contributionHead}>
                <div>
                  <span className={`${styles.actionPill} ${styles.tone_source}`}>
                    {t('仅候选')}
                  </span>
                  <span className={styles.platformPill}>{entry.authority}</span>
                  <span className={styles.platformPill}>{entry.sourceType}</span>
                  <span className={styles.platformPill}>{t('不能直接支持主张')}</span>
                  <span className={styles.platformPill}>{t('需要编目')}</span>
                </div>
                <div className={styles.metaLine}>{entry.contributionId ?? entry.sourceId}</div>
              </div>
              <div className={styles.claimBlock}>
                <span>{entry.urlHosts?.join(' · ') ?? t('候选来源占位')}</span>
                <p>{entry.sourceName}</p>
              </div>
              {entry.claimText && (
                <div className={styles.reasonBlock}>
                  <span>{t('相关主张')}</span>
                  <p>{entry.claimText}</p>
                </div>
              )}
              <div className={styles.evidenceGrid}>
                <div>
                  <span>{t('提交人')}</span>
                  <p>{entry.submittedBy ?? '—'}</p>
                </div>
                <div>
                  <span>{t('来源页面')}</span>
                  <p>{entry.sourcePageUrl ?? '—'}</p>
                </div>
                <div>
                  <span>{t('状态')}</span>
                  <p>{entry.defaultTrustState}</p>
                </div>
              </div>
              <div className={styles.reasonBlock}>
                <p>{entry.notes}</p>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  )
}

function EvidenceSegmentReviewView() {
  const copy = useAdminCopy()
  const t = copy.t
  const api = useAdminApi()
  const [segments, setSegments] = useState<KNEEvidenceSegmentReviewItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({})
  const [relations, setRelations] = useState<Record<string, string>>({})
  const [reviewingId, setReviewingId] = useState<string | null>(null)
  const [segmentSourceFilter, setSegmentSourceFilter] = useState('all')
  const [segmentCouncilFilter, setSegmentCouncilFilter] = useState('all')
  const [segmentReviewStatusFilter, setSegmentReviewStatusFilter] = useState('all')

  const loadSegments = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const data = await api.get('/evidence-segments/review?limit=50') as {
        segments: KNEEvidenceSegmentReviewItem[]
        count: number
        source: string
      }
      setSegments(data.segments)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { loadSegments() }, [loadSegments])

  const authoritative = segments.filter(segment => segment.sourceAuthority === 'authoritative').length
  const councils = Array.from(new Set(segments.map(segment => segment.council).filter(Boolean))).length
  const segmentSourceOptions = Array.from(new Set(segments.map(segment => segment.sourceId ?? segment.sourceName ?? segment.sourceTitle ?? 'unknown'))).sort()
  const segmentCouncilOptions = Array.from(new Set(segments.map(segment => segment.council || 'unknown'))).sort()
  const visibleSegments = segments.filter(segment => {
    const sourceKey = segment.sourceId ?? segment.sourceName ?? segment.sourceTitle ?? 'unknown'
    const councilKey = segment.council || 'unknown'
    return (segmentSourceFilter === 'all' || sourceKey === segmentSourceFilter)
      && (segmentCouncilFilter === 'all' || councilKey === segmentCouncilFilter)
      && (segmentReviewStatusFilter === 'all' || segment.reviewStatus === segmentReviewStatusFilter)
  })
  const readableText = (value: string) => {
    const cleaned = value.replace(/\uFFFD+/g, ' ').replace(/[�]{2,}/g, ' ').replace(/\s+/g, ' ').trim()
    return cleaned.length > 0 ? cleaned : t('不可读内容已省略')
  }
  const fmt = (s: string) => new Date(s).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
  const reviewSegment = async (segment: KNEEvidenceSegmentReviewItem, status: 'reviewed' | 'rejected') => {
    const note = reviewNotes[segment.id]?.trim()
    if (!note) {
      setError(t('复核备注不能为空。'))
      return
    }
    setReviewingId(segment.id); setError(null)
    try {
      await api.post('/evidence-segments/review', {
        segmentId: segment.id,
        legacyKnowledgeId: segment.legacyKnowledgeId,
        status,
        relation: relations[segment.id] ?? segment.relation ?? 'context',
        reviewNote: note,
      })
      setReviewNotes(prev => {
        const next = { ...prev }
        delete next[segment.id]
        return next
      })
      await loadSegments()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setReviewingId(null)
    }
  }

  return (
    <div className={styles.stack}>
      <section className={styles.heroPanel}>
        <div>
          <p className={styles.kicker}>Evidence Layer</p>
          <h1 className={styles.heroTitle}>{t('证据片段复核')}</h1>
          <p className={styles.heroText}>
            {t('只展示结构化来源生成的 Evidence Segment 候选。当前阶段用于人工复核和 KNE Core 打样，不代表已认证。')}
          </p>
        </div>
        <div className={styles.heroMetrics}>
          <div>
            <span>{segments.length}</span>
            <small>{t('待复核片段')}</small>
          </div>
          <div>
            <span>{authoritative}</span>
            <small>{t('权威来源')}</small>
          </div>
          <div>
            <span>{councils}</span>
            <small>{t('覆盖议会')}</small>
          </div>
        </div>
      </section>

      {error && (
        <div className={styles.errorBanner}>
          ⚠ {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('复核队列')}</h2>
          <div className={styles.controlBar}>
            <select className={styles.searchInput} value={segmentSourceFilter} onChange={event => setSegmentSourceFilter(event.target.value)}>
              <option value="all">{t('全部来源')}</option>
              {segmentSourceOptions.map(source => (
                <option key={source} value={source}>{source}</option>
              ))}
            </select>
            <select className={styles.searchInput} value={segmentCouncilFilter} onChange={event => setSegmentCouncilFilter(event.target.value)}>
              <option value="all">{t('全部 Council')}</option>
              {segmentCouncilOptions.map(council => (
                <option key={council} value={council}>{council}</option>
              ))}
            </select>
            <select className={styles.searchInput} value={segmentReviewStatusFilter} onChange={event => setSegmentReviewStatusFilter(event.target.value)}>
              <option value="all">{t('全部复核状态')}</option>
              {['unreviewed', 'reviewed', 'rejected'].map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
            <button className={styles.searchBtn} onClick={loadSegments}>{t('刷新')}</button>
          </div>
        </div>

        <div className={styles.reviewBoard}>
          {loading ? (
            <div className={styles.emptyPanel}><span className={styles.pulse}>{t('加载中…')}</span></div>
          ) : visibleSegments.length === 0 ? (
            <div className={styles.emptyPanel}>{t('暂无可复核 Evidence Segment。')}</div>
          ) : visibleSegments.map(segment => (
            <article key={segment.id} className={styles.contributionCard}>
              <div className={styles.contributionHead}>
                <div>
                  <span className={`${styles.actionPill} ${segment.sourceAuthority === 'authoritative' ? styles.tone_source : styles.tone_review}`}>
                    {segment.sourceAuthority}
                  </span>
                  <span className={styles.platformPill}>{segment.sourceType}</span>
                  <span className={styles.platformPill}>{segment.reviewStatus}</span>
                  <span className={styles.platformPill}>{segment.relation}</span>
                </div>
                <div className={styles.metaLine}>
                  {segment.sourceId ?? 'unregistered'} · {segment.council} · {fmt(segment.createdAt)}
                </div>
              </div>

              <div className={styles.claimBlock}>
                <span>{segment.locator}</span>
                <p>{readableText(segment.excerpt)}</p>
              </div>

              <div className={styles.evidenceGrid}>
                <div>
                  <span>{t('来源')}</span>
                  <a href={segment.sourceUrl} target="_blank" rel="noreferrer">
                    {segment.sourceName ?? segment.sourceTitle}
                  </a>
                </div>
                <div>
                  <span>{t('领域')}</span>
                  <p>{segment.domain}</p>
                </div>
                <div>
                  <span>{t('次级议会')}</span>
                  <p>{segment.secondaryCouncil ?? '—'}</p>
                </div>
              </div>

              {segment.reviewNote && (
                <div className={styles.reasonBlock}>
                  <span>{t('审核备注')}</span>
                  <p>{segment.reviewNote}</p>
                </div>
              )}

              <div className={styles.reviewActions}>
                <select
                  className={styles.searchInput}
                  value={relations[segment.id] ?? segment.relation}
                  onChange={e => setRelations(prev => ({ ...prev, [segment.id]: e.target.value }))}
                >
                  {['supports', 'context', 'contradicts'].map(value => (
                    <option key={value} value={value}>{value}</option>
                  ))}
                </select>
                <textarea
                  className={styles.searchInput}
                  rows={2}
                  placeholder={t('填写证据片段审核备注')}
                  value={reviewNotes[segment.id] ?? ''}
                  onChange={e => setReviewNotes(prev => ({ ...prev, [segment.id]: e.target.value }))}
                />
                <div className={styles.reviewButtonRow}>
                  <button
                    className={styles.searchBtn}
                    disabled={reviewingId === segment.id}
                    onClick={() => reviewSegment(segment, 'reviewed')}
                  >
                    {t('标记 reviewed')}
                  </button>
                  <button
                    className={styles.clearBtn}
                    disabled={reviewingId === segment.id}
                    onClick={() => reviewSegment(segment, 'rejected')}
                  >
                    {t('拒绝')}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  )
}

function KNECoreTemplateView() {
  const copy = useAdminCopy()
  const t = copy.t
  const api = useAdminApi()
  const [templates, setTemplates] = useState<KNECoreTemplateDraft[]>([])
  const [candidates, setCandidates] = useState<KNECoreTemplateCandidate[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [statusNotes, setStatusNotes] = useState<Record<string, string>>({})
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null)
  const [exportTemplateId, setExportTemplateId] = useState<string | null>(null)
  const [exportingTemplateId, setExportingTemplateId] = useState<string | null>(null)
  const [exportPayloads, setExportPayloads] = useState<Record<string, string>>({})
  const [coreReviewNotes, setCoreReviewNotes] = useState<Record<string, string>>({})
  const [reviewingCoreId, setReviewingCoreId] = useState<string | null>(null)
  const [templateSearch, setTemplateSearch] = useState('')
  const [templateStatusFilter, setTemplateStatusFilter] = useState<KNECoreTemplateStatus | 'all'>('all')
  const [templateTrustStateFilter, setTemplateTrustStateFilter] = useState<KNECoreTemplateDraft['trustState'] | 'all'>('all')
  const [candidateSearch, setCandidateSearch] = useState('')
  const [candidateCouncilFilter, setCandidateCouncilFilter] = useState('all')
  const [candidateTrustStateFilter, setCandidateTrustStateFilter] = useState<KNECoreTemplateDraft['trustState'] | 'all'>('all')
  const [form, setForm] = useState({
    claimText: '',
    domain: 'computer_information_science',
    council: 'computing_information',
    verificationContext: 'technical_security',
    trustState: 'source_supported' as KNECoreTemplateDraft['trustState'],
    evidenceSegmentIds: '',
    reviewNote: '',
  })

  const loadTemplates = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const [data, candidateData] = await Promise.all([
        api.get('/kne-core/templates') as Promise<{ templates: KNECoreTemplateDraft[] }>,
        api.get('/kne-core/template-candidates?limit=20') as Promise<{ candidates: KNECoreTemplateCandidate[] }>,
      ])
      setTemplates(data.templates.map(template => ({
        ...template,
        templateStatus: normalizeTemplateStatus(template.templateStatus),
      })))
      setCandidates(candidateData.candidates)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { loadTemplates() }, [loadTemplates])

  const createTemplate = async () => {
    const evidenceSegmentIds = form.evidenceSegmentIds
      .split(',')
      .map(item => item.trim())
      .filter(Boolean)
    if (form.claimText.trim().length < 8 || evidenceSegmentIds.length === 0 || !form.reviewNote.trim()) {
      setError(t('Claim、Evidence Segment 和 review note 都必须填写。'))
      return
    }
    setSaving(true); setError(null)
    try {
      await api.post('/kne-core/templates', {
        ...form,
        claimText: form.claimText.trim(),
        evidenceSegmentIds,
        reviewNote: form.reviewNote.trim(),
      })
      setForm(prev => ({ ...prev, claimText: '', evidenceSegmentIds: '', reviewNote: '' }))
      await loadTemplates()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setSaving(false)
    }
  }

  const fmt = (s: string) => new Date(s).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
  const useCandidate = (candidate: KNECoreTemplateCandidate) => {
    setForm({
      claimText: candidate.claimText,
      domain: candidate.domain,
      council: candidate.council,
      verificationContext: candidate.verificationContext,
      trustState: candidate.trustState,
      evidenceSegmentIds: candidate.evidenceSegmentIds.join(', '),
      reviewNote: candidate.reviewNote,
    })
  }
  const advanceTemplate = async (
    template: KNECoreTemplateDraft,
    status: 'reviewed_template' | 'core_candidate' | 'rejected',
  ) => {
    const note = statusNotes[template.id]?.trim()
    if (!note) {
      setError(t('状态审核备注不能为空。'))
      return
    }
    setUpdatingStatusId(template.id); setError(null)
    try {
      await api.patch(`/kne-core/templates/${template.id}/status`, { status, reviewNote: note })
      setStatusNotes(prev => {
        const next = { ...prev }
        delete next[template.id]
        return next
      })
      await loadTemplates()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setUpdatingStatusId(null)
    }
  }
  const canMarkReviewed = (template: KNECoreTemplateDraft) => template.templateStatus === 'draft'
  const canMarkCoreCandidate = (template: KNECoreTemplateDraft) =>
    template.templateStatus === 'reviewed_template'
    && template.trustState === 'source_supported'
    && template.evidenceSegmentIds.length > 0
    && Boolean(template.reviewPath?.length)
  const canRejectTemplate = (template: KNECoreTemplateDraft) =>
    template.templateStatus === 'draft' || template.templateStatus === 'reviewed_template'
  const visibleTemplates = templates.filter(template => {
    const search = templateSearch.trim().toLowerCase()
    const searchable = [
      template.claimText,
      template.domain,
      template.council,
      template.verificationContext,
      template.createdBy,
      template.reviewNote,
    ].join(' ').toLowerCase()
    return (!search || searchable.includes(search))
      && (templateStatusFilter === 'all' || template.templateStatus === templateStatusFilter)
      && (templateTrustStateFilter === 'all' || template.trustState === templateTrustStateFilter)
  })
  const candidateCouncilOptions = Array.from(new Set(candidates.map(candidate => candidate.council || 'unknown'))).sort()
  const visibleCandidates = candidates.filter(candidate => {
    const search = candidateSearch.trim().toLowerCase()
    const searchable = [
      candidate.claimText,
      candidate.domain,
      candidate.council,
      candidate.verificationContext,
      candidate.sourceTitle,
      candidate.sourceId,
      candidate.sourceType,
      candidate.sourceAuthority,
      candidate.reviewNote,
    ].filter(Boolean).join(' ').toLowerCase()
    return (!search || searchable.includes(search))
      && (candidateCouncilFilter === 'all' || candidate.council === candidateCouncilFilter)
      && (candidateTrustStateFilter === 'all' || candidate.trustState === candidateTrustStateFilter)
  })
  const coreCandidates = visibleTemplates.filter(template =>
    template.templateStatus === 'core_candidate'
    || template.templateStatus === 'accepted_candidate'
    || (template.templateStatus === 'rejected' && template.coreReviewDecision === 'reject_candidate'))
  const toggleExport = async (template: KNECoreTemplateDraft) => {
    if (exportTemplateId === template.id) {
      setExportTemplateId(null)
      return
    }
    setExportingTemplateId(template.id); setError(null)
    try {
      const data = await api.get(`/kne-core/templates/${template.id}/export`) as unknown
      setExportPayloads(prev => ({ ...prev, [template.id]: JSON.stringify(data, null, 2) }))
      setExportTemplateId(template.id)
    } catch (e) {
      setExportTemplateId(null)
      setError((e as Error).message)
    } finally {
      setExportingTemplateId(null)
    }
  }
  const recordCoreReview = async (
    template: KNECoreTemplateDraft,
    decision: 'accept_candidate' | 'hold_candidate' | 'reject_candidate',
  ) => {
    const note = coreReviewNotes[template.id]?.trim()
    if (!note) {
      setError(t('二次复核备注不能为空。'))
      return
    }
    setReviewingCoreId(template.id); setError(null)
    try {
      await api.post(`/kne-core/templates/${template.id}/core-review`, { decision, reviewNote: note })
      setCoreReviewNotes(prev => {
        const next = { ...prev }
        delete next[template.id]
        return next
      })
      await loadTemplates()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setReviewingCoreId(null)
    }
  }

  return (
    <div className={styles.stack}>
      <section className={styles.heroPanel}>
        <div>
          <p className={styles.kicker}>Review Layer</p>
          <h1 className={styles.heroTitle}>{t('复核模板草稿')}</h1>
          <p className={styles.heroText}>
            {t('用 reviewed Evidence Segment 组装 KNE Core 复核路径草稿。草稿只用于打样，不代表已进入正式 KNE Core。')}
          </p>
        </div>
        <div className={styles.heroMetrics}>
          <div>
            <span>{templates.length}</span>
            <small>{t('模板草稿')}</small>
          </div>
          <div>
            <span>{templates.filter(item => item.trustState === 'source_supported').length}</span>
            <small>{t('来源支持')}</small>
          </div>
          <div>
            <span>{coreCandidates.length}</span>
            <small>{t('核心候选')}</small>
          </div>
        </div>
      </section>

      {error && (
        <div className={styles.errorBanner}>
          ⚠ {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('创建复核草稿')}</h2>
          <button className={styles.searchBtn} disabled={saving} onClick={createTemplate}>
            {t('保存草稿')}
          </button>
        </div>
        <div className={styles.reviewActions}>
          <textarea
            className={styles.searchInput}
            rows={3}
            placeholder={t('Claim 文本')}
            value={form.claimText}
            onChange={e => setForm(prev => ({ ...prev, claimText: e.target.value }))}
          />
          <div className={styles.evidenceGrid}>
            <input className={styles.searchInput} value={form.domain} onChange={e => setForm(prev => ({ ...prev, domain: e.target.value }))} />
            <input className={styles.searchInput} value={form.council} onChange={e => setForm(prev => ({ ...prev, council: e.target.value }))} />
            <input className={styles.searchInput} value={form.verificationContext} onChange={e => setForm(prev => ({ ...prev, verificationContext: e.target.value }))} />
          </div>
          <div className={styles.evidenceGrid}>
            <select className={styles.searchInput} value={form.trustState} onChange={e => setForm(prev => ({ ...prev, trustState: e.target.value as KNECoreTemplateDraft['trustState'] }))}>
              {['source_supported', 'needs_review', 'high_risk', 'conflicted'].map(value => (
                <option key={value} value={value}>{value}</option>
              ))}
            </select>
            <input
              className={styles.searchInput}
              placeholder={t('证据片段 ID 列表')}
              value={form.evidenceSegmentIds}
              onChange={e => setForm(prev => ({ ...prev, evidenceSegmentIds: e.target.value }))}
            />
            <input
              className={styles.searchInput}
            placeholder={t('审核备注')}
              value={form.reviewNote}
              onChange={e => setForm(prev => ({ ...prev, reviewNote: e.target.value }))}
            />
          </div>
        </div>
      </section>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('已复核片段候选')}</h2>
          <div className={styles.controlBar}>
            <span className={styles.sectionHint}>
              {t('从 reviewed/supports 的 Evidence Segment 生成，点击后填入草稿表单。')}
            </span>
            <input
              className={styles.searchInput}
              placeholder={t('搜索候选 Claim / 来源 / Context')}
              value={candidateSearch}
              onChange={event => setCandidateSearch(event.target.value)}
            />
            <select className={styles.searchInput} value={candidateCouncilFilter} onChange={event => setCandidateCouncilFilter(event.target.value)}>
              <option value="all">{t('全部 Council')}</option>
              {candidateCouncilOptions.map(council => (
                <option key={council} value={council}>{council}</option>
              ))}
            </select>
            <select className={styles.searchInput} value={candidateTrustStateFilter} onChange={event => setCandidateTrustStateFilter(event.target.value as KNECoreTemplateDraft['trustState'] | 'all')}>
              <option value="all">{t('全部可信状态')}</option>
              {['source_supported', 'needs_review', 'high_risk', 'conflicted'].map(state => (
                <option key={state} value={state}>{trustStateLabel(state, t)}</option>
              ))}
            </select>
          </div>
        </div>
        <div className={styles.reviewBoard}>
          {candidates.length === 0 ? (
            <div className={styles.emptyPanel}>{t('暂无 reviewed segment 候选。')}</div>
          ) : visibleCandidates.length === 0 ? (
            <div className={styles.emptyPanel}>{t('当前筛选下没有候选。')}</div>
          ) : visibleCandidates.map(candidate => (
            <article key={candidate.evidenceSegmentIds.join('|')} className={styles.contributionCard}>
              <div className={styles.contributionHead}>
                <div>
                  <span className={`${styles.actionPill} ${styles.tone_support}`}>{trustStateLabel(candidate.trustState, t)}</span>
                  <span className={styles.platformPill}>{candidate.verificationContext}</span>
                </div>
                <button className={styles.searchBtn} onClick={() => useCandidate(candidate)}>
                  {t('使用候选')}
                </button>
              </div>
              <div className={styles.claimBlock}>
                <span>{candidate.sourceTitle ?? candidate.council}</span>
                <p>{candidate.claimText}</p>
              </div>
              <div className={styles.evidenceGrid}>
                <div>
                  <span>{t('领域')}</span>
                  <p>{candidate.domain}</p>
                </div>
                <div>
                  <span>{t('议会')}</span>
                  <p>{candidate.council}</p>
                </div>
                <div>
                  <span>{t('证据')}</span>
                  <p>{candidate.evidenceSegmentIds.join(', ')}</p>
                </div>
              </div>
              <div className={styles.evidenceGrid}>
                <div>
                  <span>{t('来源')}</span>
                  <p>{candidate.sourceId ?? '—'}</p>
                </div>
                <div>
                  <span>{t('来源类型')}</span>
                  <p>{candidate.sourceType ?? '—'} · {candidate.sourceAuthority ?? '—'}</p>
                </div>
                <div>
                  <span>{t('质量门槛')}</span>
                  <p>{candidate.qualityChecks?.join(', ') ?? '—'}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('复核草稿列表')}</h2>
          <div className={styles.controlBar}>
            <input
              className={styles.searchInput}
              placeholder={t('搜索 Claim / Council / Context')}
              value={templateSearch}
              onChange={event => setTemplateSearch(event.target.value)}
            />
            <select className={styles.searchInput} value={templateStatusFilter} onChange={event => setTemplateStatusFilter(event.target.value as KNECoreTemplateStatus | 'all')}>
              <option value="all">{t('全部模板状态')}</option>
              {['draft', 'reviewed_template', 'core_candidate', 'accepted_candidate', 'rejected'].map(status => (
                <option key={status} value={status}>{templateStatusLabel(status as KNECoreTemplateStatus, t)}</option>
              ))}
            </select>
            <select className={styles.searchInput} value={templateTrustStateFilter} onChange={event => setTemplateTrustStateFilter(event.target.value as KNECoreTemplateDraft['trustState'] | 'all')}>
              <option value="all">{t('全部可信状态')}</option>
              {['source_supported', 'needs_review', 'high_risk', 'conflicted'].map(state => (
                <option key={state} value={state}>{trustStateLabel(state, t)}</option>
              ))}
            </select>
            <button className={styles.searchBtn} onClick={loadTemplates}>{t('刷新')}</button>
          </div>
        </div>
        <div className={styles.reviewBoard}>
          {loading ? (
            <div className={styles.emptyPanel}><span className={styles.pulse}>{t('加载中…')}</span></div>
          ) : visibleTemplates.length === 0 ? (
            <div className={styles.emptyPanel}>{t('暂无 KNE Core 模板草稿。')}</div>
          ) : visibleTemplates.map(template => (
            <article key={template.id} className={styles.contributionCard}>
              <div className={styles.contributionHead}>
                <div>
                  <span className={`${styles.actionPill} ${template.trustState === 'source_supported' ? styles.tone_support : styles.tone_review}`}>
                    {trustStateLabel(template.trustState, t)}
                  </span>
                  <span className={styles.platformPill}>{templateStatusLabel(template.templateStatus, t)}</span>
                  <span className={styles.platformPill}>{template.version}</span>
                </div>
                <div className={styles.metaLine}>{template.council} · {template.verificationContext} · {fmt(template.createdAt)}</div>
              </div>
              <div className={styles.claimBlock}>
                <span>{t('主张')}</span>
                <p>{template.claimText}</p>
              </div>
              <div className={styles.evidenceGrid}>
                <div>
                  <span>{t('领域')}</span>
                  <p>{template.domain}</p>
                </div>
                <div>
                  <span>{t('证据片段 ID')}</span>
                  <p>{template.evidenceSegmentIds.join(', ')}</p>
                </div>
                <div>
                  <span>{t('创建人')}</span>
                  <p>{template.createdBy || '—'}</p>
                </div>
              </div>
              <div className={styles.reasonBlock}>
                <p>{template.reviewNote}</p>
              </div>
              {template.coreReviewDecision && (
                <div className={styles.reasonBlock}>
                  <span>{t('二次复核')}</span>
                  <p>{template.coreReviewDecision} · {template.coreReviewedBy || '—'} · {template.coreReviewedAt || '—'}</p>
                  <p>{template.coreReviewNote}</p>
                </div>
              )}
              {template.coreReviewHistory && template.coreReviewHistory.length > 0 && (
                <div className={styles.reasonBlock}>
                  <span>{t('复核历史')}</span>
                  {template.coreReviewHistory.map(review => (
                    <p key={review.id}>
                      {review.decision} · {templateStatusLabel(review.nextTemplateStatus, t)} · {review.reviewedBy || '—'} · {fmt(review.createdAt)}
                      {review.reviewNote ? ` · ${review.reviewNote}` : ''}
                    </p>
                  ))}
                </div>
              )}
              {template.statusReviewHistory && template.statusReviewHistory.length > 0 && (
                <div className={styles.reasonBlock}>
                  <span>{t('状态历史')}</span>
                  {template.statusReviewHistory.map(review => (
                    <p key={review.id}>
                      {templateStatusLabel(review.fromStatus, t)} → {templateStatusLabel(review.nextTemplateStatus, t)} · {review.reviewedBy || '—'} · {fmt(review.createdAt)}
                      {review.reviewNote ? ` · ${review.reviewNote}` : ''}
                    </p>
                  ))}
                </div>
              )}
              {template.reviewPath && template.reviewPath.length > 0 && (
                <div className={styles.reasonBlock}>
                  <span>{t('复核路径')}</span>
                  {template.reviewPath.map(item => (
                    <p key={item.evidenceSegmentId}>
                      {item.evidenceSegmentId} · {item.sourceId ?? '—'} · {item.sourceType ?? '—'} · {item.sourceAuthority ?? '—'} · {item.relation ?? '—'}
                    </p>
                  ))}
                </div>
              )}
              <div className={styles.reviewActions}>
                <textarea
                  className={styles.searchInput}
                  rows={2}
                  placeholder={t('填写状态推进备注')}
                  disabled={!canRejectTemplate(template)}
                  value={statusNotes[template.id] ?? ''}
                  onChange={e => setStatusNotes(prev => ({ ...prev, [template.id]: e.target.value }))}
                />
                <div className={styles.reviewButtonRow}>
                  <button
                    className={styles.searchBtn}
                    disabled={updatingStatusId === template.id || !canMarkReviewed(template)}
                    onClick={() => advanceTemplate(template, 'reviewed_template')}
                    title={canMarkReviewed(template) ? undefined : t('只有 draft 可以标记 reviewed。')}
                  >
                    {t('标记 reviewed template')}
                  </button>
                  <button
                    className={styles.searchBtn}
                    disabled={updatingStatusId === template.id || !canMarkCoreCandidate(template)}
                    onClick={() => advanceTemplate(template, 'core_candidate')}
                    title={canMarkCoreCandidate(template) ? undefined : t('只有 reviewed_template 且具备复核路径的 source_supported 草稿可以进入 core candidate。')}
                  >
                    {t('标记 core candidate')}
                  </button>
                  <button
                    className={styles.clearBtn}
                    disabled={updatingStatusId === template.id || !canRejectTemplate(template)}
                    onClick={() => advanceTemplate(template, 'rejected')}
                    title={canRejectTemplate(template) ? undefined : t('核心候选必须通过二次复核处理。')}
                  >
                    {t('拒绝模板')}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('Core Candidate 复核')}</h2>
          <p className={styles.sectionHint}>
            {t('只导出候选给人工或 Claude 二次审核，不写入正式 KNE Core。')}
          </p>
        </div>
        <div className={styles.reviewBoard}>
          {coreCandidates.length === 0 ? (
            <div className={styles.emptyPanel}>{t('暂无 core candidate。')}</div>
          ) : coreCandidates.map(template => (
            <article key={`core-${template.id}`} className={styles.contributionCard}>
              <div className={styles.contributionHead}>
                <div>
                  <span className={`${styles.actionPill} ${styles.tone_support}`}>{templateStatusLabel(template.templateStatus, t)}</span>
                  <span className={styles.platformPill}>{template.council}</span>
                  <span className={styles.platformPill}>{template.verificationContext}</span>
                </div>
                <button
                  className={styles.searchBtn}
                  disabled={exportingTemplateId === template.id}
                  onClick={() => { void toggleExport(template) }}
                >
                  {exportingTemplateId === template.id ? t('导出中…') : exportTemplateId === template.id ? t('收起导出') : t('导出审核包')}
                </button>
              </div>
              <div className={styles.claimBlock}>
                <span>{t('主张')}</span>
                <p>{template.claimText}</p>
              </div>
              <div className={styles.evidenceGrid}>
                <div>
                  <span>{t('可信状态')}</span>
                  <p>{trustStateLabel(template.trustState, t)}</p>
                </div>
                <div>
                  <span>{t('证据片段 ID')}</span>
                  <p>{template.evidenceSegmentIds.join(', ')}</p>
                </div>
                <div>
                  <span>{t('复核路径')}</span>
                  <p>{template.reviewPath?.length ?? 0}</p>
                </div>
                <div>
                  <span>{t('二次复核')}</span>
                  <p>{template.coreReviewDecision || '—'} · {template.coreReviewHistory?.length ?? 0}</p>
                </div>
              </div>
              {exportTemplateId === template.id && (
                <textarea
                  className={styles.exportBox}
                  readOnly
                  rows={14}
                  value={exportPayloads[template.id] ?? ''}
                />
              )}
              {template.templateStatus === 'core_candidate' ? (
                <div className={styles.reviewActions}>
                  <textarea
                    className={styles.searchInput}
                    rows={2}
                    placeholder={t('填写二次复核备注')}
                    value={coreReviewNotes[template.id] ?? ''}
                    onChange={e => setCoreReviewNotes(prev => ({ ...prev, [template.id]: e.target.value }))}
                  />
                  <div className={styles.reviewButtonRow}>
                    <button
                      className={styles.searchBtn}
                      disabled={reviewingCoreId === template.id}
                      onClick={() => { void recordCoreReview(template, 'accept_candidate') }}
                    >
                      {t('二审通过')}
                    </button>
                    <button
                      className={styles.searchBtn}
                      disabled={reviewingCoreId === template.id}
                      onClick={() => { void recordCoreReview(template, 'hold_candidate') }}
                    >
                      {t('暂缓候选')}
                    </button>
                    <button
                      className={styles.clearBtn}
                      disabled={reviewingCoreId === template.id}
                      onClick={() => { void recordCoreReview(template, 'reject_candidate') }}
                    >
                      {t('拒绝候选')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className={styles.reasonBlock}>
                  <span>{t('终态候选')}</span>
                  <p>{t('该候选已完成二次复核，只保留导出和历史查看。')}</p>
                </div>
              )}
            </article>
          ))}
        </div>
      </section>
    </div>
  )
}

function SourceGapGovernanceView() {
  const copy = useAdminCopy()
  const t = copy.t
  const api = useAdminApi()
  const [metrics, setMetrics] = useState<BetaMetrics | null>(null)
  const [loading, setLoading] = useState(false)
  const [creatingKey, setCreatingKey] = useState<string | null>(null)
  const [updatingTaskId, setUpdatingTaskId] = useState<string | null>(null)
  const [bulkUpdatingTasks, setBulkUpdatingTasks] = useState(false)
  const [taskNotes, setTaskNotes] = useState<Record<string, string>>({})
  const [bulkTaskNote, setBulkTaskNote] = useState('')
  const [taskStatusFilter, setTaskStatusFilter] = useState('active')
  const [error, setError] = useState<string | null>(null)

  const loadMetrics = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const data = await api.get('/beta-metrics') as { metrics: BetaMetrics }
      setMetrics(data.metrics)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => { loadMetrics() }, [loadMetrics])

  const gaps = metrics?.sourceGapRanking ?? []
  const tasks = metrics?.sourceGapTasks ?? []
  const visibleTasks = tasks.filter(task => {
    if (taskStatusFilter === 'all') return true
    if (taskStatusFilter === 'active') return !['done', 'ignored'].includes(task.taskStatus)
    return task.taskStatus === taskStatusFilter
  })
  const openTaskCount = tasks.filter(task => task.taskStatus === 'open').length
  const inProgressTaskCount = tasks.filter(task => task.taskStatus === 'in_progress').length
  const doneTaskCount = tasks.filter(task => task.taskStatus === 'done').length
  const ignoredTaskCount = tasks.filter(task => task.taskStatus === 'ignored').length
  const actionableVisibleTasks = visibleTasks.filter(task => !['done', 'ignored'].includes(task.taskStatus))
  const coverage = metrics?.coverageMatrix ?? []
  const highPriority = gaps.filter(gap => gap.priority === 'high').length
  const councils = Array.from(new Set(gaps.map(gap => gap.council).filter(Boolean)))
  const taskKey = (gap: BetaMetrics['sourceGapRanking'][number]) =>
    `${gap.council}|${gap.domain}|${gap.claimType}|${gap.trustState}`
  const activeTaskKeys = new Set(tasks
    .filter(task => ['open', 'in_progress'].includes(task.taskStatus))
    .map(task => `${task.council}|${task.domain}|${task.claimType}|${task.trustState}`))
  const fmt = (s: string) => new Date(s).toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
  const createTask = async (gap: BetaMetrics['sourceGapRanking'][number]) => {
    const key = taskKey(gap)
    setCreatingKey(key); setError(null)
    try {
      await api.post('/source-gap-tasks', {
        domain: gap.domain,
        council: gap.council || 'unknown',
        claimType: gap.claimType,
        trustState: gap.trustState,
        priority: gap.priority,
        missingSourceType: gap.missingSourceType || 'authoritative source',
        sampleClaim: gap.sampleClaim,
        nextAction: gap.nextAction,
      })
      await loadMetrics()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setCreatingKey(null)
    }
  }
  const updateTaskStatus = async (
    task: BetaMetrics['sourceGapTasks'][number],
    taskStatus: 'in_progress' | 'done' | 'ignored',
  ) => {
    setUpdatingTaskId(task.id); setError(null)
    try {
      await api.patch(`/source-gap-tasks/${task.id}/status`, {
        taskStatus,
        reviewNote: taskNotes[task.id]?.trim() || undefined,
      })
      setTaskNotes(prev => {
        const next = { ...prev }
        delete next[task.id]
        return next
      })
      await loadMetrics()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setUpdatingTaskId(null)
    }
  }
  const bulkUpdateTasks = async (taskStatus: 'in_progress' | 'done' | 'ignored') => {
    if (actionableVisibleTasks.length === 0) return
    setBulkUpdatingTasks(true); setError(null)
    try {
      await Promise.all(actionableVisibleTasks.map(task => api.patch(`/source-gap-tasks/${task.id}/status`, {
        taskStatus,
        reviewNote: bulkTaskNote.trim() || undefined,
      })))
      setBulkTaskNote('')
      await loadMetrics()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBulkUpdatingTasks(false)
    }
  }

  return (
    <div className={styles.stack}>
      <section className={styles.heroPanel}>
        <div>
          <p className={styles.kicker}>Source Layer</p>
          <h1 className={styles.heroTitle}>{t('来源缺口治理')}</h1>
          <p className={styles.heroText}>
            {t('按真实 Verify 缺口决定 KNE 下一步补源方向。用户验证行为只作为需求信号，不直接成为知识认证。')}
          </p>
        </div>
        <div className={styles.heroMetrics}>
          <div>
            <span>{gaps.length}</span>
            <small>{t('缺口组')}</small>
          </div>
          <div>
            <span>{highPriority}</span>
            <small>{t('高优先级')}</small>
          </div>
          <div>
            <span>{metrics?.sourceHintCoverageRate ?? '—'}%</span>
            <small>{t('来源线索覆盖')}</small>
          </div>
          <div>
            <span>{metrics?.sourceHintWithoutSupport7d ?? '—'}</span>
            <small>{t('线索未支持')}</small>
          </div>
        </div>
      </section>

      {error && (
        <div className={styles.errorBanner}>
          ⚠ {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <div className={styles.moduleGrid}>
        <article className={styles.moduleCard}>
          <span className={styles.moduleLabel}>{t('数据来源')}</span>
          <strong>{metrics?.verifyCount7d ?? '—'}</strong>
          <p>{t('最近 7 天 Verify 事件。用于定位缺口，不用于直接认证知识。')}</p>
        </article>
        <article className={styles.moduleCard}>
          <span className={styles.moduleLabel}>{t('涉及 Council')}</span>
          <strong>{councils.length}</strong>
          <p>{councils.length > 0 ? councils.join(', ') : t('暂无缺口 council。')}</p>
        </article>
        <article className={styles.moduleCard}>
          <span className={styles.moduleLabel}>Coverage Matrix</span>
          <strong>{coverage.filter(item => item.canSupportSourceSupported).length}/{coverage.length}</strong>
          <p>{t('十二议会中具备可支持来源的覆盖数量；只表示来源能力，不等于 KNE Core 认证。')}</p>
        </article>
        <article className={styles.moduleCard}>
          <span className={styles.moduleLabel}>{t('补源原则')}</span>
          <strong>{t('来源与证据')}</strong>
          <p>{t('先补权威来源和可引用来源，候选来源不直接升级为 KNE 认证。')}</p>
        </article>
      </div>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>KNE Coverage Matrix</h2>
          <p className={styles.sectionHint}>{t('每个 council 只记录三件事：能否支持 source_supported、最高频缺口、下一类应补来源。')}</p>
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Council</th>
                <th>{t('可支持')}</th>
                <th>{t('来源数')}</th>
                <th>{t('结构化片段来源')}</th>
                <th>{t('来源库联动')}</th>
                <th>{t('最高频缺口')}</th>
                <th>{t('下一类来源')}</th>
              </tr>
            </thead>
            <tbody>
              {coverage.length === 0 ? (
                <tr>
                  <td colSpan={7}>{loading ? t('加载中…') : t('暂无 Coverage Matrix 数据')}</td>
                </tr>
              ) : coverage.map(item => (
                <tr key={item.council}>
                  <td>{item.council}</td>
                  <td>{t(item.canSupportSourceSupported ? '是' : '否')}</td>
                  <td>{item.supportedSourceCount}</td>
                  <td>{item.structuredSegmentSourceCount}</td>
                  <td>{item.supportingSourceIds?.length ? item.supportingSourceIds.join(', ') : '—'}</td>
                  <td>{item.topSourceMissingScenario}</td>
                  <td>{item.nextMissingSourceType}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('来源缺口排行')}</h2>
          <button className={styles.searchBtn} onClick={loadMetrics} disabled={loading}>
            {loading ? t('刷新中…') : t('刷新')}
          </button>
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('优先级')}</th>
                <th>Council</th>
                <th>{t('领域')}</th>
                <th>{t('Claim 类型')}</th>
                <th>{t('状态')}</th>
                <th>{t('次数')}</th>
                <th>{t('缺失来源类型')}</th>
                <th>{t('治理动作')}</th>
                <th>{t('样例')}</th>
                <th>{t('任务')}</th>
              </tr>
            </thead>
            <tbody>
              {gaps.length === 0 ? (
                <tr>
                  <td colSpan={10}>{loading ? t('加载中…') : t('暂无来源缺口数据')}</td>
                </tr>
              ) : gaps.map((gap, index) => (
                <tr key={`${gap.council}-${gap.domain}-${gap.claimType}-${gap.trustState}-${index}`}>
                  <td>{priorityLabel(gap.priority, t)}</td>
                  <td>{gap.council || 'unknown'}</td>
                  <td>{gap.domain}</td>
                  <td>{gap.claimType}</td>
                  <td>{trustStateLabel(gap.trustState, t)}</td>
                  <td>{gap.count}</td>
                  <td>{gap.missingSourceType || 'authoritative source'}</td>
                  <td>{gap.nextAction ? t(gap.nextAction) : t('继续观察')}</td>
                  <td>{gap.sampleClaim || '—'}</td>
                  <td>
                    {activeTaskKeys.has(taskKey(gap)) ? t('已建任务') : (
                      <button
                        className={styles.searchBtn}
                        onClick={() => createTask(gap)}
                        disabled={creatingKey === taskKey(gap)}
                      >
                        {creatingKey === taskKey(gap) ? t('创建中…') : t('生成任务')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('补源任务')}</h2>
          <div className={styles.controlBar}>
            <span className={styles.sectionHint}>
              {t('未处理')} {openTaskCount} · {t('处理中')} {inProgressTaskCount} · {t('已完成')} {doneTaskCount} · {t('已忽略')} {ignoredTaskCount}
            </span>
            <select
              className={styles.searchInput}
              value={taskStatusFilter}
              onChange={event => setTaskStatusFilter(event.target.value)}
            >
              <option value="active">{t('未完成任务')}</option>
              <option value="all">{t('全部任务')}</option>
              <option value="open">{t('未处理')}</option>
              <option value="in_progress">{t('处理中')}</option>
              <option value="done">{t('已完成')}</option>
              <option value="ignored">{t('已忽略')}</option>
            </select>
            <input
              className={styles.searchInput}
              value={bulkTaskNote}
              maxLength={300}
              placeholder={t('批量备注（可选）')}
              onChange={event => setBulkTaskNote(event.target.value)}
            />
            <button
              className={styles.searchBtn}
              disabled={bulkUpdatingTasks || actionableVisibleTasks.length === 0}
              onClick={() => { void bulkUpdateTasks('in_progress') }}
            >
              {t('批量处理中')}
            </button>
            <button
              className={styles.searchBtn}
              disabled={bulkUpdatingTasks || actionableVisibleTasks.length === 0}
              onClick={() => { void bulkUpdateTasks('done') }}
            >
              {t('批量完成')}
            </button>
            <button
              className={styles.clearBtn}
              disabled={bulkUpdatingTasks || actionableVisibleTasks.length === 0}
              onClick={() => { void bulkUpdateTasks('ignored') }}
            >
              {t('批量忽略')}
            </button>
          </div>
        </div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('时间')}</th>
                <th>{t('状态')}</th>
                <th>Council</th>
                <th>{t('领域')}</th>
                <th>{t('缺失来源类型')}</th>
                <th>{t('动作')}</th>
                <th>{t('复核备注')}</th>
                <th>{t('处理时间')}</th>
                <th>{t('治理动作')}</th>
              </tr>
            </thead>
            <tbody>
              {visibleTasks.length === 0 ? (
                <tr>
                  <td colSpan={9}>{t('暂无补源任务')}</td>
                </tr>
              ) : visibleTasks.map(task => {
                const isTerminalTask = ['done', 'ignored'].includes(task.taskStatus)
                return (
                <tr key={task.id}>
                  <td>{fmt(task.createdAt)}</td>
                  <td>{sourceGapTaskStatusLabel(task.taskStatus, t)}</td>
                  <td>{task.council}</td>
                  <td>{task.domain}</td>
                  <td>{task.missingSourceType}</td>
                  <td>{task.nextAction ? t(task.nextAction) : t('补充来源')}</td>
                  <td>{task.taskReviewNote || task.taskReviewedBy || '—'}</td>
                  <td>{task.taskReviewedAt ? fmt(task.taskReviewedAt) : '—'}</td>
                  <td>
                    <div className={styles.reviewActions}>
                      <textarea
                        value={taskNotes[task.id] ?? ''}
                        onChange={event => setTaskNotes(prev => ({ ...prev, [task.id]: event.target.value }))}
                        maxLength={300}
                        placeholder={t('处理备注（可选）')}
                      />
                      <div className={styles.reviewButtonRow}>
                        <button
                          className={styles.searchBtn}
                          disabled={updatingTaskId === task.id || isTerminalTask || task.taskStatus === 'in_progress'}
                          onClick={() => { void updateTaskStatus(task, 'in_progress') }}
                        >
                          {t('处理中')}
                        </button>
                        <button
                          className={styles.searchBtn}
                          disabled={updatingTaskId === task.id || isTerminalTask}
                          onClick={() => { void updateTaskStatus(task, 'done') }}
                        >
                          {t('完成')}
                        </button>
                        <button
                          className={styles.clearBtn}
                          disabled={updatingTaskId === task.id || isTerminalTask}
                          onClick={() => { void updateTaskStatus(task, 'ignored') }}
                        >
                          {t('忽略')}
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

function TrustPolicyView() {
  const copy = useAdminCopy()
  const t = copy.t
  return (
    <div className={styles.stack}>
      <section className={styles.heroPanel}>
        <div>
          <p className={styles.kicker}>Trust Policy v0</p>
          <h1 className={styles.heroTitle}>{t('可信状态规则')}</h1>
          <p className={styles.heroText}>
            {t('Spark 不认证“真理”，只输出当前 KNE 来源层能支持的可信状态、证据缺口和人工复核要求。')}
          </p>
        </div>
      </section>
      <div className={styles.policyGrid}>
        {[
          ['source_supported', '存在可引用来源，且来源等级满足当前主张类型。'],
          ['source_missing', '未找到候选来源或来源不足以支持主张。'],
          ['needs_review', '高风险、冲突、时效敏感或来源等级不足。'],
          ['conflicted', '候选来源之间存在直接冲突。'],
          ['outdated', '证据可能过期，需要更新来源。'],
          ['high_risk', '医疗、安全、法律等领域默认进入强复核。'],
        ].map(([state, body]) => (
          <article key={state} className={styles.policyCard}>
            <strong>{state}</strong>
            <p>{t(body)}</p>
          </article>
        ))}
      </div>
    </div>
  )
}

function PlatformOpsView() {
  const copy = useAdminCopy()
  const t = copy.t
  return (
    <div className={styles.stack}>
      <section className={styles.heroPanel}>
        <div>
          <p className={styles.kicker}>Source Layer</p>
          <h1 className={styles.heroTitle}>{t('来源识别')}</h1>
          <p className={styles.heroText}>
            {t('第一阶段采用通用选中文本验证：用户主动选择 AI 或网页内容后验证；平台只做轻量来源识别，不维护 DOM 深适配。')}
          </p>
        </div>
      </section>
      <div className={styles.moduleGrid}>
        {[
          ['通用选中文本', '核心入口：用户主动选择文本后通过右键或 Side Panel 验证。'],
          ['来源识别', '记录 sourcePlatform、sourceUrl 和标题，用于回溯验证场景。'],
          ['AI 工作流', '覆盖 ChatGPT、千问、Claude、豆包、Gemini 等常用 AI 页面。'],
          ['普通网页', '支持资料页、文章页和搜索结果中的主动选中文本验证。'],
        ].map(([title, body]) => (
          <article key={title} className={styles.moduleCard}>
            <span className={styles.moduleLabel}>{t('平台')}</span>
            <strong>{t(title)}</strong>
            <p>{t(body)}</p>
          </article>
        ))}
      </div>
    </div>
  )
}

// ── KNE 审核视图 ──────────────────────────────────────────────────────────────

function KNEReviewView() {
  const copy = useAdminCopy()
  const t = copy.t
  const api = useKNEAdminApi()
  const [items, setItems] = useState<KNEContribution[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [platform, setPlatform] = useState<'all' | KNEContributionPlatform>('all')
  const [action, setAction] = useState<'all' | KNEContributionAction>('all')
  const [reviewNotes, setReviewNotes] = useState<Record<string, string>>({})
  const [reviewingId, setReviewingId] = useState<string | null>(null)

  const loadPending = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const params = new URLSearchParams({ limit: '100' })
      if (platform !== 'all') params.set('sourcePlatform', platform)
      if (action !== 'all') params.set('action', action)
      const data = await api.get(`/contributions/pending?${params}`) as {
        contributions: KNEContribution[]
        count: number
      }
      setItems(data.contributions)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platform, action])

  useEffect(() => { loadPending() }, [loadPending])

  const review = async (item: KNEContribution, status: 'accepted' | 'rejected') => {
    const note = reviewNotes[item.id]?.trim()
    if (!note) {
      setError(t('复核备注不能为空。'))
      return
    }
    setReviewingId(item.id); setError(null)
    try {
      await api.post(`/contributions/${item.id}/review`, { status, note })
      setReviewNotes(prev => {
        const next = { ...prev }
        delete next[item.id]
        return next
      })
      await loadPending()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setReviewingId(null)
    }
  }

  const overdue = items.filter(item => Date.now() - new Date(item.createdAt).getTime() > 48 * 60 * 60 * 1000).length
  const platforms: Array<'all' | KNEContributionPlatform> = ['all', 'chatgpt', 'qwen', 'web', 'claude', 'doubao', 'unknown']
  const actions: Array<'all' | KNEContributionAction> = ['all', 'add_source', 'challenge_claim', 'correct_claim', 'submit_claim', 'support_claim']
  const fmt = (s: string) => new Date(s).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
  const shortId = (id: string) => id.slice(0, 8)
  const preview = (item: KNEContribution) =>
    item.claimText ?? item.correctionText ?? item.challengeReason ?? item.sourceTitle ?? item.sourceUrl ?? item.note ?? '—'
  const highSignal = items.filter(item => item.sourceHintAuthority === 'official' || item.action === 'challenge_claim').length

  return (
    <div className={styles.stack}>
      <section className={styles.heroPanel}>
        <div>
          <p className={styles.kicker}>{t('复核队列')}</p>
          <h1 className={styles.heroTitle}>{t('KNE 贡献审核')}</h1>
          <p className={styles.heroText}>
            {t('处理用户从 AI 使用现场提交的来源、纠错、质疑和知识主张。接受只代表进入 KNE 后续处理，不代表自动认证为真。')}
          </p>
        </div>
        <div className={styles.heroMetrics}>
          <div>
            <span>{items.length}</span>
            <small>{t('待处理')}</small>
          </div>
          <div>
            <span>{overdue}</span>
            <small>{t('超过 48h')}</small>
          </div>
          <div>
            <span>{highSignal}</span>
            <small>{t('高价值线索')}</small>
          </div>
        </div>
      </section>

      <div className={styles.statsGrid}>
        {[
          { label: '待处理', value: items.length, accent: true },
          { label: '超过 48h', value: overdue, accent: overdue > 0 },
          { label: '平台', value: platform },
          { label: '操作', value: action },
        ].map(c => (
          <div key={c.label} className={`${styles.statCard} ${c.accent ? styles.statCardAccent : ''}`}>
            <span className={styles.statValue}>{c.value}</span>
            <span className={styles.statLabel}>{t(c.label)}</span>
          </div>
        ))}
      </div>

      {error && (
        <div className={styles.errorBanner}>
          ⚠ {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      <section className={styles.tableSection}>
        <div className={styles.tableHeader}>
          <h2 className={styles.tableTitle}>{t('复核队列')}</h2>
          <div className={styles.controlBar}>
            <select className={styles.searchInput} value={platform} onChange={e => setPlatform(e.target.value as typeof platform)}>
              {platforms.map(value => <option key={value} value={value}>{value === 'all' ? t('全部平台') : PLATFORM_LABEL[value]}</option>)}
            </select>
            <select className={styles.searchInput} value={action} onChange={e => setAction(e.target.value as typeof action)}>
              {actions.map(value => <option key={value} value={value}>{value === 'all' ? t('全部动作') : t(ACTION_LABEL[value])}</option>)}
            </select>
            <button className={styles.searchBtn} onClick={loadPending}>{t('刷新')}</button>
          </div>
        </div>

        <div className={styles.reviewBoard}>
          {loading ? (
            <div className={styles.emptyPanel}><span className={styles.pulse}>{t('加载中…')}</span></div>
          ) : items.length === 0 ? (
            <div className={styles.emptyPanel}>{t('当前没有待审核贡献。')}</div>
          ) : items.map(item => (
            <article key={item.id} className={styles.contributionCard}>
              <div className={styles.contributionHead}>
                <div>
                  <span className={`${styles.actionPill} ${styles[`tone_${ACTION_TONE[item.action]}`]}`}>
                    {t(ACTION_LABEL[item.action])}
                  </span>
                  <span className={styles.platformPill}>
                    {PLATFORM_LABEL[item.sourcePlatform ?? 'unknown']}
                  </span>
                </div>
                <div className={styles.metaLine}>
                  #{shortId(item.id)} · user {shortId(item.userId)} · {fmt(item.createdAt)}
                </div>
              </div>

              <div className={styles.claimBlock}>
                  <span>{t('主张 / 信号')}</span>
                <p>{preview(item)}</p>
              </div>

              <div className={styles.evidenceGrid}>
                <div>
                  <span>{t('来源')}</span>
                  {item.sourceUrl ? (
                    <a href={item.sourceUrl} target="_blank" rel="noreferrer">
                      {item.sourceTitle ?? item.sourceUrl}
                    </a>
                  ) : (
                    <p>{t('未提供来源链接')}</p>
                  )}
                </div>
                <div>
                  <span>{t('来源线索')}</span>
                  <p>
                    {item.sourceHintLabel ?? '—'}
                    {item.sourceHintAuthority ? ` · ${item.sourceHintAuthority}` : ''}
                    {item.sourceHintType ? ` · ${item.sourceHintType}` : ''}
                  </p>
                </div>
                <div>
                  <span>{t('上下文')}</span>
                  {item.sourcePageUrl ? (
                    <a href={item.sourcePageUrl} target="_blank" rel="noreferrer">
                      {item.sourcePageTitle ?? 'source page'}
                    </a>
                  ) : (
                    <p>{item.sourceHintReason ?? item.note ?? '—'}</p>
                  )}
                </div>
              </div>

              {(item.challengeReason || item.correctionText) && (
                <div className={styles.reasonBlock}>
                  {item.challengeReason && <p>{t('质疑')}：{item.challengeReason}</p>}
                  {item.correctionText && <p>{t('纠正')}：{item.correctionText}</p>}
                </div>
              )}

              <div className={styles.reviewActions}>
                <textarea
                  className={styles.searchInput}
                  rows={2}
                  placeholder={t('填写审核备注，说明接受或拒绝的原因')}
                  value={reviewNotes[item.id] ?? ''}
                  onChange={e => setReviewNotes(prev => ({ ...prev, [item.id]: e.target.value }))}
                />
                <div>
                  <div className={styles.reviewButtonRow}>
                    <button
                      className={styles.searchBtn}
                      disabled={reviewingId === item.id}
                      onClick={() => review(item, 'accepted')}
                    >
                      {t('接受')}
                    </button>
                    <button
                      className={styles.clearBtn}
                      disabled={reviewingId === item.id}
                      onClick={() => review(item, 'rejected')}
                    >
                      {t('拒绝')}
                    </button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  )
}

// ── Nav config ────────────────────────────────────────────────────────────────

type NavView = 'overview' | 'retrievalQuality' | 'kneReview' | 'sourceGaps' | 'evidenceSegments' | 'coreTemplates' | 'sources' | 'trustPolicy' | 'platforms'

const KNE_NAV_ITEMS: { view: NavView; icon: string; label: string; section?: string }[] = [
  { view: 'overview',    icon: 'OV', label: 'KNE 工作台', section: 'Overview' },
  { view: 'retrievalQuality', icon: 'RQ', label: '检索质量', section: 'Evidence Layer' },
  { view: 'sources',     icon: 'SR', label: '来源库', section: 'Source Layer' },
  { view: 'sourceGaps',  icon: 'SG', label: '来源缺口', section: 'Source Layer' },
  { view: 'platforms',   icon: 'AD', label: '来源识别', section: 'Source Layer' },
  { view: 'evidenceSegments', icon: 'ES', label: '证据片段复核', section: 'Evidence Layer' },
  { view: 'kneReview',   icon: 'RV', label: '贡献审核', section: 'Review Layer' },
  { view: 'coreTemplates', icon: 'KT', label: '核心候选复核', section: 'Review Layer' },
  { view: 'trustPolicy', icon: 'TP', label: '可信策略', section: 'Policy Layer' },
]

// ── Main KNE Admin Page ───────────────────────────────────────────────────────

export default function KneAdmin() {
  const navigate = useNavigate()
  const { token, email, logout } = useAuth()
  const adminI18n = useAdminI18n('kne-admin-root')

  const [activeView,   setActiveView]   = useState<NavView>('overview')
  const [error,        setError]        = useState<string | null>(null)

  useEffect(() => {
    if (!token) navigate('/admin-login')
  }, [token, navigate])

  const renderContent = () => {
    switch (activeView) {
      case 'overview':
        return <KNEOverviewView />

      case 'retrievalQuality':
        return <RetrievalQualityView />

      case 'kneReview':
        return <KNEReviewView />

      case 'sourceGaps':
        return <SourceGapGovernanceView />

      case 'evidenceSegments':
        return <EvidenceSegmentReviewView />

      case 'coreTemplates':
        return <KNECoreTemplateView />

      case 'sources':
        return <SourceOpsView />

      case 'trustPolicy':
        return <TrustPolicyView />

      case 'platforms':
        return <PlatformOpsView />

      default:
        return (
          <div style={{ padding: '32px 0', fontSize: 13, color: '#2a4a2a', fontFamily: 'var(--font-mono)' }}>
            {activeView} · {adminI18n.t('未配置模块')}
          </div>
        )
    }
  }

  return (
    <div id="kne-admin-root" className={styles.root}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.logo}>KNE</span>
          <span className={styles.headerSep}>/</span>
          <span className={styles.headerTitle}>KNOWLEDGE GOVERNANCE</span>
        </div>
        <div className={styles.headerRight}>
          <span>{email ?? 'admin'}</span>
          <button className={styles.backBtn} type="button" onClick={adminI18n.switchLocale}>
            {adminI18n.switchLabel}
          </button>
          <button className={styles.backBtn} onClick={() => navigate('/admin')}>
            Spark Ops
          </button>
          <button
            className={styles.backBtn}
            onClick={() => {
              logout()
              navigate('/admin-login')
            }}
          >
            {adminI18n.t('退出')}
          </button>
        </div>
      </header>

      <div className={styles.main}>
        <nav className={styles.nav}>
          {KNE_NAV_ITEMS.map(item => (
            <div key={item.view}>
              {item.section && <div className={styles.navSection}>{adminI18n.t(item.section)}</div>}
              <button
                className={`${styles.navItem} ${activeView === item.view ? styles.navActive : ''}`}
                onClick={() => setActiveView(item.view)}
              >
                <span className={styles.navIcon}>{item.icon}</span>
                {adminI18n.t(item.label)}
              </button>
            </div>
          ))}
        </nav>

        <main className={styles.content}>
          {error && (
            <div className={styles.errorBanner}>
              ⚠ {error}
              <button onClick={() => setError(null)}>✕</button>
            </div>
          )}
          {renderContent()}
        </main>
      </div>
    </div>
  )
}
